import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { fetchAddressSLP } from "../../helpers/getSLP";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaExternalLinkAlt } from "react-icons/fa";

const ScholarCard = styled.div`
  border: 1px solid #aaa;
  border-radius: 8px;
  padding: 4px 8px;
  margin: 10px 0;
  p {
    margin: 0;
  }
`;

const Button = styled.button`
  padding: 6px 8px;
  background: gold;
  border: none;
  border-radius: 4px;
  color: black;
  cursor: pointer;
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
`;

const CardBody = styled.div`
  font-weight: 400;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export type ScholarProps = { ethAddress: string; nickname: string };

type Props = ScholarProps & {
  onRemove: (ethAddress: string) => void;
  index: number;
  viewMode?: "Card" | "Table";
};

type SlpDetails = {
  total: number;
  claimable_total: number;
  last_claimed_item_at: number;
};

const ScholarOverviewFC: React.FC<Props> = (props) => {
  const { nickname, ethAddress, onRemove, viewMode = "Card", index } = props;
  const [slpDetails, setSlpDetails] = useState<SlpDetails>();
  const [loading, setLoading] = useState<boolean>(false);

  const fetchSLP = useCallback(() => {
    setLoading(true);
    if (!slpDetails && !loading)
      fetchAddressSLP(ethAddress)
        .then((response) => {
          setSlpDetails(response);
        })
        .finally(() => setLoading(false));
  }, [ethAddress, loading, slpDetails]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (!slpDetails) {
        setLoading(true);
        fetchAddressSLP(ethAddress)
          .then((response) => {
            setSlpDetails(response);
          })
          .finally(() => setLoading(false));
      }
    }, index * 500);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [ethAddress, index, slpDetails]);

  const lastClaimedAt = useMemo(() => {
    if (slpDetails) {
      return moment(moment.unix(slpDetails.last_claimed_item_at).utc(false).format("LL"));
    }
  }, [slpDetails]);

  const daysSinceLastClaim = useMemo(() => {
    if (lastClaimedAt) {
      console.log(lastClaimedAt.format("MMM DD, YYYY HH:mm:ss"));
      console.log(moment.utc().format("MMM DD, YYYY HH:mm:ss"));
      return moment.utc().diff(lastClaimedAt, "days");
    }
  }, [lastClaimedAt]);

  // return (
  //   <tr>
  //     <td>{nickname}</td>
  //     <td align="center">
  //       <a
  //         href={`https://lunacia.skymavis.com/game-api/clients/${ethAddress}/items/1`}
  //         target="_blank"
  //         rel="noopener noreferrer"
  //       >
  //         Open link&nbsp;
  //         <FaExternalLinkAlt />
  //         <span style={{ fontSize: "0.9rem" }}>(Look for total amount)</span>
  //       </a>
  //     </td>
  //     <td align="center">
  //       <Button onClick={() => onRemove(ethAddress)}>
  //         <RiDeleteBinLine />
  //       </Button>
  //     </td>
  //   </tr>
  // );

  if (viewMode === "Card") {
    return (
      <ScholarCard>
        <CardHead>
          <p>{nickname}</p>
          <p style={{ maxWidth: 150, overflow: "hidden" }}>
            {ethAddress.substr(0, 5)}.....{ethAddress.substr(35)}
          </p>
        </CardHead>
        <CardBody>
          {!slpDetails && !loading ? (
            <p>Tap to Fetch SLP</p>
          ) : (
            <>
              <p>
                <span>Total:&nbsp;</span>
                {slpDetails ? `${slpDetails.total} SLP` : "Loading..."}
              </p>
              <p>
                <span>Claimable:&nbsp;</span>
                {slpDetails
                  ? `${slpDetails.total - slpDetails.claimable_total} SLP`
                  : "Loading..."}
              </p>
              <p>
                <span>Available:&nbsp;</span>
                {slpDetails
                  ? `${slpDetails.claimable_total} SLP`
                  : "Loading..."}
              </p>
              <p>
                <span>Since last claim:&nbsp;</span>
                {slpDetails ? `${daysSinceLastClaim} days` : "Loading..."}
              </p>
              <p>
                <span>Daily Average:&nbsp;</span>
                {slpDetails && daysSinceLastClaim
                  ? `${(slpDetails.total / daysSinceLastClaim).toFixed(0)} SLP`
                  : "Loading..."}
              </p>
              <p>
                <span>Last Claimed:&nbsp;</span>
                {lastClaimedAt
                  ? lastClaimedAt.format("MM/DD h:mmA")
                  : "Loading..."}
              </p>
            </>
          )}
        </CardBody>
        <CardFooter>
          <Button onClick={() => onRemove(ethAddress)}>Remove</Button>
        </CardFooter>
      </ScholarCard>
    );
  } else {
    return (
      <tr onClick={fetchSLP}>
        <td>{nickname}</td>
        <td>{daysSinceLastClaim && `${daysSinceLastClaim} days`}</td>
        <td>
          {daysSinceLastClaim && slpDetails
            ? `${(slpDetails.total / daysSinceLastClaim).toFixed(0)} SLP`
            : "N/A"}
        </td>
        <td>
          {!slpDetails
            ? loading
              ? "Loading..."
              : "N/A"
            : `${slpDetails.total} SLP`}
        </td>
        <td align="center">
          <Button onClick={() => onRemove(ethAddress)}>
            <RiDeleteBinLine />
          </Button>
        </td>
      </tr>
    );
  }
};

const ScholarOverview = React.memo(ScholarOverviewFC);

export default ScholarOverview;
